import { Link } from "react-router-dom";
import { Download, GitCompareArrows, Loader2 } from "lucide-react";
import { Reveal } from "../Reveal";

const LABEL_STYLE = {
  Thriving: "bg-gold/15 text-gold-dark",
  "Finding Balance": "bg-sage text-forest",
  "Out of Balance": "bg-[#F1E9D8] text-[#7A6A45]",
  "Survival Mode": "bg-forest text-ivory",
  "Insufficient Information": "bg-charcoal/5 text-taupe",
};

const DIM_SHORT = { PHY: "Physical", ENV: "Environmental", EMO: "Emotional", SPI: "Spiritual" };

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

export const WellnessTimeline = ({ entries, selected, onToggleCompare, onDownloadPdf, downloadingId }) => {
  const dayCounts = entries.reduce((acc, e) => {
    const d = fmt(e.created_at);
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="wellness-timeline" className="relative">
      <span
        aria-hidden="true"
        className="absolute bottom-6 left-[19px] top-6 w-px bg-forest/15"
      />
      <div className="flex flex-col gap-8">
        {entries.map((e, i) => {
          const isSelected = selected.includes(e.assessment_id);
          const isFirst = i === 0;
          const isLatest = i === entries.length - 1;
          return (
            <Reveal key={e.assessment_id} delay={i * 0.05}>
              <div className="relative flex gap-5 pl-1">
                <span
                  className={`relative z-10 mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${
                    isLatest ? "border-gold bg-gold/20" : "border-forest/20 bg-white"
                  }`}
                  aria-hidden="true"
                >
                  <span className="h-2.5 w-2.5 rounded-full bg-forest" />
                </span>
                <div
                  data-testid={`timeline-entry-${e.assessment_id}`}
                  className={`flex-1 rounded-[1.75rem] border p-6 shadow-[0_12px_30px_rgba(23,63,53,0.07)] ${
                    isLatest ? "border-gold/40 bg-white/85" : "border-forest/10 bg-white/75"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {isFirst && (
                      <span className="rounded-full bg-sage px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-forest">
                        Starting Point
                      </span>
                    )}
                    {isLatest && entries.length > 1 && (
                      <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-gold-dark">
                        Most Recent Check-In
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-forest">
                        {fmt(e.created_at)}
                        {dayCounts[fmt(e.created_at)] > 1 && (
                          <span className="ml-2 text-xs font-normal text-taupe">
                            {fmtTime(e.created_at)}
                          </span>
                        )}
                      </p>
                      <span
                        className={`mt-2 inline-flex rounded-full px-4 py-1.5 font-serif text-base ${LABEL_STYLE[e.overall] || LABEL_STYLE["Insufficient Information"]}`}
                      >
                        {e.overall}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Link
                        to={`/results/${e.assessment_id}`}
                        data-testid={`timeline-view-${e.assessment_id}`}
                        className="text-xs font-semibold text-gold-dark underline-offset-4 hover:underline"
                      >
                        Open saved report
                      </Link>
                      {onDownloadPdf && (
                        <button
                          data-testid={`timeline-pdf-${e.assessment_id}`}
                          onClick={() => onDownloadPdf(e.assessment_id)}
                          disabled={downloadingId === e.assessment_id}
                          aria-label="Download PDF report"
                          className="inline-flex items-center gap-1.5 rounded-full border border-forest/15 px-3.5 py-2 text-xs font-semibold text-forest transition-colors hover:border-gold disabled:opacity-50"
                        >
                          {downloadingId === e.assessment_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                          PDF
                        </button>
                      )}
                      <button
                        data-testid={`timeline-compare-${e.assessment_id}`}
                        onClick={() => onToggleCompare(e.assessment_id)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                          isSelected
                            ? "border-gold bg-gold/15 text-gold-dark"
                            : "border-forest/20 text-forest hover:border-gold"
                        }`}
                      >
                        <GitCompareArrows className="h-3.5 w-3.5" />
                        {isSelected ? "Selected" : "Compare"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {["PHY", "ENV", "EMO", "SPI"].map((c) => (
                      <span
                        key={c}
                        className="rounded-full bg-forest/6 px-3 py-1 text-[11px] font-semibold text-forest"
                      >
                        {DIM_SHORT[c]}: {e.dimensions?.[c]?.label || "n/a"}
                      </span>
                    ))}
                    <span className="rounded-full bg-sage px-3 py-1 text-[11px] font-semibold text-taupe">
                      History: contextual
                    </span>
                  </div>
                  {(e.strengths?.length > 0 || e.attention?.length > 0) && (
                    <div className="mt-4 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                      {e.strengths?.length > 0 && (
                        <p className="text-charcoal/70">
                          <span className="font-semibold text-gold-dark">Supporting: </span>
                          {e.strengths.join(" · ")}
                        </p>
                      )}
                      {e.attention?.length > 0 && (
                        <p className="text-charcoal/70">
                          <span className="font-semibold text-forest">Attention: </span>
                          {e.attention.join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                  {e.intention?.text && (
                    <p className="mt-4 border-l-2 border-gold/50 pl-4 font-serif text-sm italic text-forest/85">
                      "{e.intention.text}"
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
};
